import { HoverShadow } from './styled';
import { useTracking } from 'react-tracking';
import { RouterLink } from '@redux-first-router-link';
import PNV from '@/components/PositiveNegativeValue';
import type { BestValuationRatio } from '@/utilities/getBestValuationRatio';
import {
  ValuationRatio,
  GrowthTooltip,
} from '@/pages/Stocks/components/StockTable';
import loadable from '@loadable/component';
import { Box, Grid, Text, useTheme } from '@simplywallst/ui-core';
import Media from 'react-media';
import { toLocaleUrl } from '@/pages/CompanyReport/utils/toLocaleUrl';
import { useLocale } from '@/hooks/useLocale';
import WithTooltip from '../WithTooltip';

const WatchlistAddRemoveCompanyToggle = loadable(
  () => import('@/components/WatchlistAddRemoveCompanyToggle'),
  { ssr: false }
);

const Snowflake = loadable(() => import('@/components/DeprecatedSnowflake'));
export interface Company {
  canonicalUrl: string;
  companyId: string;
  shortName: string;
  snowflakePoints: [number, number, number, number, number];
  tickerSymbol: string;
  uniqueSymbol: string;
  return1YrAbs: number | null;
  return1YrAbsFormatted: string;
  return7D: number | null;
  return7DFormatted: string;
  marketCapFormatted: string;
  peFormatted: string;
  pbFormatted: string;
  psFormatted: string;
  sharePriceFormatted: string;
  bestValuationRatio: BestValuationRatio;
  analystCount: number | null;
  earningsGrowth: number | null;
  earningsGrowthFormatted: string;
  salesGrowth: number | null;
  salesGrowthFormatted: string;
  analystsPriceTargetFormatted: string;
  industryLabel: string;
}

export interface Props {
  company: Company;
  section: string;
  index: number;
  hideIndustryCol?: boolean;
}

export const Tile = ({
  index,
  company: {
    canonicalUrl,
    companyId,
    shortName,
    snowflakePoints,
    tickerSymbol,
    uniqueSymbol,
    return1YrAbs,
    return1YrAbsFormatted,
    return7D,
    return7DFormatted,
    marketCapFormatted,
    peFormatted,
    psFormatted,
    pbFormatted,
    sharePriceFormatted,
    bestValuationRatio,
    analystCount,
    earningsGrowth,
    earningsGrowthFormatted,
    salesGrowth,
    salesGrowthFormatted,
    analystsPriceTargetFormatted,
    industryLabel,
  },
  section,
}: Props) => {
  const { trackEvent } = useTracking();
  const theme = useTheme();
  const locale = useLocale();

  return (
    <Box position="relative" role="listitem">
      <RouterLink
        to={toLocaleUrl(locale, canonicalUrl)}
        noUnderline
        inheritColor
        onClick={() =>
          trackEvent({
            action: 'click',
            subject: `${section}_next_steps_company`,
            uniqueSymbol: uniqueSymbol,
            companyId,
            index,
          })
        }
        data-cy-id="link"
      >
        <HoverShadow>
          <Grid
            maxHeight={370}
            backgroundColor="phantom"
            borderRadiusSm="x1"
            templateRows="1fr"
            templateColumns="180px 1fr"
            templateColumnsSm="1fr"
          >
            <Box>
              <Grid justifyContent="center">
                <Box width="130px" padding="x1" paddingSm="x0" marginTopSm="x1">
                  <Snowflake points={snowflakePoints} />
                </Box>
              </Grid>
              <Media
                query={{ maxWidth: theme.xsMaxRaw }}
                render={() => (
                  <Box padding="x1">
                    <Text
                      typography="xSmall"
                      textAlign="center"
                      data-cy-id="company-name"
                      lineClamp={2}
                    >
                      {shortName}
                    </Text>
                  </Box>
                )}
              />
            </Box>
            <Grid gap="8px" padding="x1">
              <Media
                query={{ minWidth: theme.smMinRaw }}
                render={() => (
                  <>
                    <Grid templateColumns="1fr max-content">
                      <Text typography="tiny" color="chart01">
                        {tickerSymbol}
                      </Text>
                      <Text typography="tiny" color="chart01">
                        {industryLabel}
                      </Text>
                    </Grid>
                    <Text typography="small" isTruncated>
                      {shortName}
                    </Text>
                  </>
                )}
              />

              <Grid templateColumns="45% 1fr 1fr" alignItems="center">
                <Box>
                  <Text typography="tiny" color="whiteRGBA" opacity={0.5}>
                    Price
                  </Text>
                  <Text typography="tiny" color="white">
                    {sharePriceFormatted}
                  </Text>
                </Box>
                <Box>
                  <Text typography="tiny" color="whiteRGBA" opacity={0.5}>
                    7D
                  </Text>
                  <Text typography="tiny">
                    <PNV value={return7D}>{return7DFormatted}</PNV>
                  </Text>
                </Box>
                <Box>
                  <Text typography="tiny" color="whiteRGBA" opacity={0.5}>
                    1Y
                  </Text>
                  <Text typography="tiny">
                    <PNV value={return1YrAbs}>{return1YrAbsFormatted}</PNV>
                  </Text>
                </Box>
              </Grid>
              <Grid templateColumns="45% 1fr 1fr" alignItems="center">
                <Box>
                  <Text typography="tiny" color="whiteRGBA" opacity={0.5}>
                    Mkt Cap
                  </Text>
                  <Text typography="tiny">{marketCapFormatted}</Text>
                </Box>
                <Box>
                  <WithTooltip
                    title={
                      <ValuationRatio
                        priceToEarnings={peFormatted}
                        priceToBook={pbFormatted}
                        priceToSales={psFormatted}
                      />
                    }
                    term={bestValuationRatio.valuationType}
                  />
                  <Text typography="tiny">
                    {bestValuationRatio.valuationFormatted}
                  </Text>
                </Box>
                <Box>
                  {earningsGrowth || salesGrowth ? (
                    <WithTooltip
                      title={
                        <GrowthTooltip
                          analystCount={analystCount}
                          earningsGrowth={earningsGrowthFormatted}
                          salesGrowth={salesGrowthFormatted}
                        />
                      }
                      term={earningsGrowth ? 'E.Growth' : 'S.Growth'}
                    />
                  ) : (
                    <Text typography="tiny" color="whiteRGBA" opacity={0.5}>
                      Growth
                    </Text>
                  )}
                  <Text typography="tiny">
                    {earningsGrowth
                      ? earningsGrowthFormatted
                      : salesGrowthFormatted}
                  </Text>
                </Box>
              </Grid>
              <Box>
                <Text typography="tiny" color="whiteRGBA" opacity={0.5}>
                  Analysts Target
                </Text>
                <Text typography="tiny">{analystsPriceTargetFormatted}</Text>
              </Box>
            </Grid>
          </Grid>
        </HoverShadow>
      </RouterLink>
      <Box
        position="absolute"
        right={theme.x4}
        bottom={theme.x2}
        width={theme.x3}
        height={theme.x3}
      >
        <WatchlistAddRemoveCompanyToggle
          companyId={companyId}
          tickerSymbol={tickerSymbol}
          styleType="gentle"
          minimal
        />
      </Box>
    </Box>
  );
};
